import { useState } from 'react';
import { connect } from 'react-redux';
import toast from 'react-hot-toast';
import { Lock, Plus } from 'lucide-react';
import { SectionLegend } from 'components/Profile';
import { choiceBtnUnselected } from 'components/Profile/helper';
import { ProfileActions } from 'store/actions';
import Item from './Item';
import Tabs from './Tabs';
import { TABS, EMPTY_STATES } from './helper';

const enhancer = connect(null, { ...ProfileActions });

const Authorization = ({ contacts, revokeAuthorizedContact, reAuthorizeContact, onRefresh = null, onAdd }) => {
  const [activeTab, setActiveTab] = useState(TABS.AUTHORIZED);
  const [confirmId, setConfirmId] = useState(null);

  const { authorized = [], revoked = [] } = contacts || {};
  const isRevokedTab = activeTab === TABS.REVOKED;
  const list = isRevokedTab ? revoked : authorized;
  const { icon: EmptyIcon, iconClassName, title, description } = EMPTY_STATES[activeTab];

  const handleAction = async (id) => {
    try {
      await (isRevokedTab ? reAuthorizeContact(id) : revokeAuthorizedContact(id));

      toast.success(isRevokedTab ? 'Access restored' : 'Access revoked');
      setConfirmId(null);
      onRefresh?.();
    } catch (error: any) {
      toast.error(error.errors || 'Something went wrong. Please try again later.');
    }
  };

  return (
    <div>
      <SectionLegend optional description="We only discuss your care with you unless you name someone here, such as a spouse who books your visits. You can add or remove people anytime.">
        People we can talk to about your care
      </SectionLegend>

      <Tabs
        activeTab={activeTab}
        counts={{ [TABS.AUTHORIZED]: authorized.length, [TABS.REVOKED]: revoked.length }}
        onTabChange={setActiveTab}
      />

      <div key={activeTab} className="animate-slide-up">
        {list.length ? (
          <div className="divide-y divide-gray-100">
            {list.map((contact) => (
              <Item
                key={contact.id}
                contact={contact}
                revoked={isRevokedTab}
                confirming={confirmId === contact.id}
                onAction={() => setConfirmId(contact.id)}
                onCancel={() => setConfirmId(null)}
                onConfirm={() => handleAction(contact.id)}
              />
            ))}
          </div>
        ) : (
          <div className="mt-4 flex flex-col items-center gap-2 rounded-xl border border-dashed border-gray-200 bg-secondary-50/50 px-6 py-10 text-center">
            <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${iconClassName}`}>
              <EmptyIcon size={22} />
            </span>
            <p className="text-sm font-bold text-secondary-900">{title}</p>
            <p className="max-w-md text-xs text-secondary-500">{description}</p>
          </div>
        )}

        <button type="button" onClick={onAdd} className={`btn mt-4 h-10 min-h-0 w-full gap-1.5 rounded-xl font-semibold sm:w-auto ${choiceBtnUnselected}`}>
          <Plus size={16} strokeWidth={2.5} /> Add person
        </button>

        <p className="mt-4 flex items-start gap-2 text-xs text-secondary-400">
          <Lock size={14} className="mt-0.5 shrink-0" />
          Staff verify the caller's identity before sharing anything. Changes take effect immediately and are recorded in your patient file.
        </p>
      </div>
    </div>
  );
};

export default enhancer(Authorization);
